/**
 * Thrown from AuthService.loginWithPasskey so the UI can map failures to the
 * failing step (init → WebAuthn → verify → token → whoAmI).
 *
 * Stage `token` means the verify response had no JWT: if the API returns a
 * different field name than the generated `PasskeyAuthenticationVerifyResponse`,
 * update the OpenAPI spec and run `yarn generate:api` (do not hand-edit `core/api`).
 */
export type PasskeyLoginFailureStage = 'init' | 'webauthn' | 'verify' | 'token' | 'whoami';

function messageFromCause(rootCause: unknown): string {
  if (rootCause instanceof Error) {
    return rootCause.message;
  }
  if (typeof rootCause === 'object' && rootCause !== null && 'message' in rootCause) {
    const m = rootCause.message;
    if (typeof m === 'string' && m !== '') {
      return m;
    }
  }
  return 'Passkey sign-in failed';
}

export class PasskeyLoginError extends Error {
  readonly stage: PasskeyLoginFailureStage;
  /** Original rejection (HTTP error, DOMException, etc.). */
  readonly rootCause: unknown;

  constructor(stage: PasskeyLoginFailureStage, rootCause: unknown) {
    super(messageFromCause(rootCause));
    this.name = 'PasskeyLoginError';
    this.stage = stage;
    this.rootCause = rootCause;
  }

  static isPasskeyLoginError(value: unknown): value is PasskeyLoginError {
    return value instanceof PasskeyLoginError;
  }
}
